import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { MapContainer, TileLayer, CircleMarker, Popup, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { Landmark, Download, Loader2, Route, TrendingUp, TrendingDown, Minus, MapPinOff, X } from "lucide-react";
import { TypeInfoModal, AnalyticsPanel, HotspotWatch } from "@/components/BaltIntel";

const dLabel = (name) => L.divIcon({ className: "", html: `<div style="font:700 10px monospace;color:#93C5FD;text-shadow:0 0 4px #000;white-space:nowrap">${name.toUpperCase()}</div>`, iconSize: [80, 12], iconAnchor: [40, 6] });

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, Math.max(map.getZoom(), 14), { duration: 0.6 }); }, [target, map]);
  return null;
}

export default function BaltimoreCfs() {
  const [sum, setSum] = useState(null);
  const [heat, setHeat] = useState(null);
  const [calls, setCalls] = useState([]);
  const [district, setDistrict] = useState("");
  const [busy, setBusy] = useState(false);
  const [route, setRoute] = useState(null);
  const [trends, setTrends] = useState(null);
  const [ta, setTa] = useState(null);
  const [thr, setThr] = useState("");
  const [selKey, setSelKey] = useState(null);
  const [typeFor, setTypeFor] = useState(null);
  const [drill, setDrill] = useState(null);
  useEffect(() => {
    client.get("/baltimore/trends").then((r) => setTrends(r.data)).catch(() => {});
    client.get("/baltimore/trend-alerts").then((r) => { setTa(r.data); setThr(String(r.data.threshold)); }).catch(() => {});
  }, []);
  const saveThr = async () => {
    try {
      const { data } = await client.post("/baltimore/trend-alerts", { threshold: Number(thr) });
      setTa((p) => ({ ...p, threshold: data.threshold }));
      toast.success(`Trend alert threshold set to +${data.threshold}% MoM${data.fired_now.length ? ` — ${data.fired_now.length} alert(s) fired now` : ""}`);
      client.get("/baltimore/trend-alerts").then((r) => setTa(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  useEffect(() => { setRoute(null); }, [district]);
  const suggestRoute = async () => {
    try {
      const { data } = await client.get("/baltimore/patrol-route", { params: { district } });
      setRoute(data);
      toast.success(`Patrol loop: ${data.waypoints.length} hotspots · ~${data.loop_mi} mi`);
    } catch (e) { toast.error(e.response?.data?.detail || "No route available"); }
  };
  const load = useCallback(() => {
    client.get("/baltimore/summary").then((r) => setSum(r.data)).catch(() => {});
    client.get("/baltimore/heat", { params: district ? { district } : {} }).then((r) => setHeat(r.data)).catch(() => {});
    const callParams = drill ? { ...drill.params, limit: 1500 } : (district ? { district } : {});
    client.get("/baltimore/calls", { params: callParams }).then((r) => setCalls(r.data.calls)).catch(() => {});
  }, [district, drill]);
  useEffect(() => { load(); }, [load]);

  const doImport = async () => {
    setBusy(true);
    toast("Importing Baltimore 911 CFS (Emergency/High)… this can take ~30s");
    try {
      const { data } = await client.post("/baltimore/import", { limit: 4000 });
      toast.success(`Imported ${data.fetched} records (${data.new} new) — ${data.total} total on file`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };
  const max = Math.max(1, ...((heat?.cells || []).map((c) => c.count)));

  return (
    <Layout>
      <PageHeader title="Baltimore 911 CFS — RMS Intelligence" sub="Open Baltimore feed · Emergency & High priority · heat map by police district · Baltimore MD only"
        right={<button data-testid="baltimore-import" onClick={doImport} disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} IMPORT / REFRESH FEED
        </button>} icon={Landmark} />
      <div className="flex-1 overflow-hidden flex flex-col" data-testid="baltimore-page">
        <div className="shrink-0 px-4 py-2 border-b border-[#2C2D35] flex items-center gap-2 flex-wrap bg-[#0d0e11]">
          <button data-testid="balt-district-all" onClick={() => setDistrict("")}
            className={`px-2 py-1 text-[10px] font-head font-bold rounded-sm border ${!district ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-[#2C2D35] text-[#9CA3AF]"}`}>
            ALL {sum ? `(${sum.total})` : ""}</button>
          {(sum?.by_district || []).map(([d, n]) => (
            <button key={d} data-testid={`balt-district-${d}`} onClick={() => setDistrict(d)}
              className={`px-2 py-1 text-[10px] font-head rounded-sm border ${district === d ? "bg-[#EF4444] text-white border-[#EF4444]" : "border-[#2C2D35] text-[#9CA3AF]"}`}>
              {d} <span className="font-mono font-bold">{n}</span></button>))}
          {district && (
            <button data-testid="balt-route-btn" onClick={suggestRoute}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-head font-bold bg-[#22C55E] text-black rounded-sm">
              <Route size={11} /> SUGGEST PATROL ROUTE
            </button>)}
          {route && <span className="font-mono text-[9px] text-[#22C55E]">{route.waypoints.length} stops · ~{route.loop_mi} mi loop (advisory)</span>}
          {calls.length > 0 && (() => {
            const mapped = calls.filter((c) => c.lat != null).length;
            return (
              <span data-testid="balt-sync-counts" className="font-mono text-[9px] text-[#9CA3AF]">
                {calls.length} calls · <span className="text-[#22D3EE]">{mapped} on map</span>
                {calls.length - mapped > 0 && <span className="text-[#F59E0B]"> · {calls.length - mapped} unmapped</span>}
              </span>);
          })()}
          {sum && <span className="ml-auto font-mono text-[9px] text-[#6B7280]">{sum.note}</span>}
        </div>
        {sum && sum.total === 0 && (
          <div className="p-6 font-mono text-xs text-[#9CA3AF]">No Baltimore data on file yet — press IMPORT / REFRESH FEED to pull the latest Emergency/High 911 calls from Open Baltimore.</div>)}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          <div className="lg:w-3/5 h-72 lg:h-auto border-b lg:border-b-0 lg:border-r border-[#2C2D35]">
            <MapContainer center={[39.2988, -76.6205]} zoom={12} style={{ height: "100%", width: "100%", background: "#0d0e11" }}>
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri, OpenStreetMap contributors" />
              {Object.entries(heat?.districts || {}).map(([name, [la, ln]]) => (
                <Marker key={name} position={[la, ln]} icon={dLabel(name)} interactive={false} />))}
              {route && (
                <>
                  <Polyline positions={[...route.waypoints, route.waypoints[0]].map((w) => [w.lat, w.lng])}
                    pathOptions={{ color: "#22C55E", weight: 3, dashArray: "8 6" }} />
                  {route.waypoints.map((w) => (
                    <Marker key={w.order} position={[w.lat, w.lng]}
                      icon={L.divIcon({ className: "", html: `<div style="width:20px;height:20px;border-radius:50%;background:#22C55E;color:#04120b;font:800 11px monospace;display:flex;align-items:center;justify-content:center;border:2px solid #fff">${w.order}</div>`, iconSize: [22, 22], iconAnchor: [11, 11] })}>
                      <Popup><div className="font-mono text-xs"><b>Stop {w.order}</b> — {w.count} call(s) in cell</div></Popup>
                    </Marker>))}
                </>
              )}
              {(heat?.cells || []).map((c, i) => (
                <CircleMarker key={i} center={[c.lat, c.lng]} radius={3 + 14 * (c.count / max)}
                  pathOptions={{ color: "#EF4444", fillColor: "#F97316", fillOpacity: 0.45, weight: 0.5 }}>
                  <Popup><div className="font-mono text-xs"><b>{c.count} call(s)</b><br />{c.district} District<br />(district-level approximate)</div></Popup>
                </CircleMarker>))}
              {/* Individual call dots — synced 1:1 with the side-panel list */}
              {calls.filter((c) => c.lat != null).map((c) => {
                const isSel = selKey === c.call_key;
                return (
                  <CircleMarker key={c.call_key} center={[c.lat, c.lng]} radius={isSel ? 9 : 5}
                    pathOptions={{ color: isSel ? "#FFFFFF" : (c.priority === "Emergency" ? "#EF4444" : "#FBBF24"),
                      fillColor: c.priority === "Emergency" ? "#EF4444" : "#F59E0B",
                      fillOpacity: isSel ? 0.95 : 0.8, weight: isSel ? 2.5 : 1 }}
                    eventHandlers={{ click: () => {
                      setSelKey(c.call_key);
                      setTimeout(() => document.getElementById(`balt-row-${c.call_key}`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 50);
                    } }}>
                    <Popup>
                      <div className="font-mono text-xs" data-testid={`balt-dot-popup-${c.call_key}`}>
                        <b>{c.description}</b><br />
                        <span style={{ color: c.priority === "Emergency" ? "#DC2626" : "#B45309" }}>{c.priority}</span>
                        {c.ts ? ` · ${new Date(c.ts).toLocaleString("en-GB", { hour12: false })}` : ""}<br />
                        {c.address}{c.neighborhood ? ` · ${c.neighborhood}` : ""}<br />
                        {c.police_district} District · #{c.call_number || c.call_key}<br />
                        <i>(position district-level approximate)</i>
                      </div>
                    </Popup>
                  </CircleMarker>);
              })}
              <FlyTo target={(() => { const s = calls.find((c) => c.call_key === selKey); return s && s.lat != null ? [s.lat, s.lng] : null; })()} />
            </MapContainer>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AnalyticsPanel districts={sum?.by_district || []} onDrillBaltimore={(d) => { setDrill(d); setSelKey(null); }} />
            <HotspotWatch />
            {trends?.districts?.length > 0 && (
              <div className="border-b border-[#1E1F24]" data-testid="balt-trends">
                <div className="px-3 py-2 font-head text-[10px] font-bold text-[#9CA3AF] uppercase flex items-center gap-2 flex-wrap">
                  <span>Month-over-Month District Trends <span className="font-mono font-normal normal-case">— {trends.note}</span></span>
                  <span className="ml-auto flex items-center gap-1.5 font-mono text-[9px] normal-case">
                    Alert command at +<input data-testid="trend-threshold" value={thr} onChange={(e) => setThr(e.target.value)}
                      className="w-12 bg-[#0d0e11] border border-[#2C2D35] px-1 py-0.5 text-[10px] text-white rounded-sm outline-none" />% MoM
                    <button data-testid="trend-threshold-save" onClick={saveThr}
                      className="px-2 py-0.5 text-[9px] font-head font-bold bg-[#F59E0B] text-black rounded-sm uppercase">Set</button>
                  </span>
                </div>
                {ta?.alerts?.length > 0 && (
                  <div data-testid="trend-alerts" className="px-3 pb-1 font-mono text-[9px] text-[#EF4444]">
                    {ta.alerts.slice(0, 3).map((a) => (
                      <div key={a.id}>🚨 {a.district} +{a.mom_pct}% ({a.month}) — command notified {new Date(a.ts).toLocaleDateString("en-GB")}</div>))}
                  </div>)}
                <table className="w-full text-left mb-1">
                  <thead className="font-mono text-[9px] text-[#6B7280] uppercase">
                    <tr><th className="px-3 py-1">District</th>{trends.months.map((m) => <th key={m} className="px-2">{m.slice(2)}</th>)}<th className="px-2">MoM</th></tr>
                  </thead>
                  <tbody>
                    {trends.districts.map((d) => {
                      const T = d.trend === "up" ? TrendingUp : d.trend === "down" ? TrendingDown : Minus;
                      const col = d.trend === "up" ? "#EF4444" : d.trend === "down" ? "#22C55E" : "#9CA3AF";
                      return (
                        <tr key={d.district} className="border-b border-[#1E1F24]" data-testid={`balt-trend-${d.district}`}>
                          <td className="px-3 py-1 text-[11px] text-white">{d.district}</td>
                          {d.series.map((s) => <td key={s.month} className="px-2 font-mono text-[10px]">{s.count}</td>)}
                          <td className="px-2"><span className="flex items-center gap-1 font-mono text-[10px] font-bold" style={{ color: col }}><T size={11} />{d.mom_pct != null ? `${d.mom_pct > 0 ? "+" : ""}${d.mom_pct}%` : "—"}</span></td>
                        </tr>);
                    })}
                  </tbody>
                </table>
              </div>)}
            {sum?.top_types?.length > 0 && (
              <div className="px-3 py-2 border-b border-[#1E1F24] font-mono text-[9px] text-[#9CA3AF]">
                TOP: {sum.top_types.slice(0, 8).map(([t, n]) => `${t} (${n})`).join(" · ")}
              </div>)}
            {drill && (
              <div data-testid="balt-drill-banner" className="px-3 py-1.5 border-b border-[#22D3EE]/40 bg-[#22D3EE]/10 flex items-center gap-2 font-mono text-[10px] text-[#22D3EE]">
                <span>SHOWING CALLS COUNTED — {drill.label}</span>
                <button data-testid="balt-drill-clear" onClick={() => { setDrill(null); setSelKey(null); }}
                  className="ml-auto flex items-center gap-1 px-2 py-0.5 border border-[#22D3EE]/50 rounded-sm font-head font-bold text-[9px] hover:bg-[#22D3EE]/20"><X size={10} /> CLEAR</button>
              </div>)}
            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase sticky top-0 bg-[#0d0e11]">
                <tr><th className="px-3 py-2">Time</th><th className="px-2">Pri</th><th className="px-2">Type</th><th className="px-2">Location</th><th className="px-2">District</th></tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.call_key} id={`balt-row-${c.call_key}`} onClick={() => setSelKey(c.call_key)}
                    className={`border-b border-[#1E1F24] cursor-pointer ${selKey === c.call_key ? "bg-[#22D3EE]/15" : "hover:bg-[#141518]"}`}
                    data-testid={`balt-call-${c.call_key}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-[#6B7280] whitespace-nowrap">{c.ts ? new Date(c.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</td>
                    <td className="px-2 font-mono text-[9px] font-bold" style={{ color: c.priority === "Emergency" ? "#EF4444" : "#F59E0B" }}>{c.priority === "Emergency" ? "EMER" : "HIGH"}</td>
                    <td className="px-2 text-[11px]">
                      <button data-testid={`balt-type-btn-${c.call_key}`} onClick={(e) => { e.stopPropagation(); setTypeFor(c.description); }}
                        className="text-white hover:text-[#22D3EE] hover:underline text-left">{c.description}</button>
                    </td>
                    <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{c.address}{c.neighborhood ? ` · ${c.neighborhood}` : ""}
                      {c.lat == null && <span data-testid={`balt-unmapped-${c.call_key}`} className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-px rounded-sm border border-[#F59E0B]/60 text-[#F59E0B] text-[8px] font-bold"><MapPinOff size={8} /> UNMAPPED</span>}
                    </td>
                    <td className="px-2 font-mono text-[10px] text-[#22D3EE]">{c.police_district}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {typeFor && <TypeInfoModal description={typeFor} onClose={() => setTypeFor(null)} />}
    </Layout>
  );
}
